import type { Series } from "highcharts";

import { translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter, percentagePointFilter, yAxisChangeFilter } from "app/filters/ngFilters";
import { calcChange } from "app/UtilitiesAndConstants/UtilityFunctions/change";

import type { INumbersRecord } from "../../../types";
import { CHART_OPTIONS_META_DATA } from "../../const";
import { ETypes, type IDataPoint, type IMetaData } from "../../types";

import { ATTRIBUTES_WITH_PERCENTAGE_POINTS } from "./const";

const convertedVisitsFormatter = minVisitsAbbrFilter();

const formatValueOrNA = (value: number, formatter: (value: number) => string): string => {
    if (value === null || value === undefined || isNaN(value) || value === 0) {
        return translate("apps.common.na");
    }
    return formatter(value);
};

const resolveAboveAverageValues = (value: number, averageValue: number) => {
    if (value === averageValue) {
        return null;
    }
    return value > averageValue;
};

const resolvePeriodOverPeriodChange = (
    currentValue: number,
    previousValue: number,
    axisAttribute: keyof INumbersRecord,
): string => {
    if (ATTRIBUTES_WITH_PERCENTAGE_POINTS.includes(axisAttribute)) {
        return percentagePointFilter(currentValue, previousValue);
    }
    return yAxisChangeFilter()(calcChange(currentValue, previousValue)) as string;
};

const calculateChangeOrNA = (
    currentValue: number,
    previousValue: number,
    axisAttribute: keyof INumbersRecord,
): string => {
    if (
        currentValue === null ||
        currentValue === undefined ||
        isNaN(currentValue) ||
        currentValue === 0 ||
        previousValue === null ||
        previousValue === undefined ||
        isNaN(previousValue) ||
        previousValue === 0
    ) {
        return translate("apps.common.na");
    }
    return resolvePeriodOverPeriodChange(currentValue, previousValue, axisAttribute);
};

type TooltipRow = {
    metric: string;
    value: string;
    aboveAverage: boolean | null;
};

const appendConvertedVisitsRowForConversionChart = (
    rows: TooltipRow[],
    type: ETypes,
    rawCurrentData?: Partial<INumbersRecord>,
) => {
    if (type !== ETypes.ConversionRate || !rawCurrentData) {
        return;
    }
    const cv = rawCurrentData.ConvertedVisits ?? 0;
    rows.push({
        metric: translate("market.research.players.bubble.conversion.converted.visits.tooltip"),
        value: formatValueOrNA(cv, convertedVisitsFormatter),
        aboveAverage: null,
    });
};

type PeriodOverPeriodTooltipRow = {
    metric: string;
    previousPeriodValue: string;
    currentPeriodValue: string;
    change: string;
};

const appendConvertedVisitsPeriodOverPeriodRow = (
    rows: PeriodOverPeriodTooltipRow[],
    type: ETypes,
    rawCurrentData: Partial<INumbersRecord> | undefined,
    rawCompareRecord: Partial<INumbersRecord> | undefined,
) => {
    if (type !== ETypes.ConversionRate) {
        return;
    }
    if (!rawCompareRecord) {
        rows.push({
            metric: translate("market.research.players.bubble.conversion.converted.visits.tooltip"),
            previousPeriodValue: translate("apps.common.na"),
            currentPeriodValue: formatValueOrNA(
                rawCurrentData?.ConvertedVisits ?? 0,
                convertedVisitsFormatter,
            ),
            change: translate("apps.common.na"),
        });
        return;
    }
    const prevCv = rawCompareRecord.ConvertedVisits ?? 0;
    const currCv = rawCurrentData?.ConvertedVisits ?? 0;
    rows.push({
        metric: translate("market.research.players.bubble.conversion.converted.visits.tooltip"),
        previousPeriodValue: formatValueOrNA(prevCv, convertedVisitsFormatter),
        currentPeriodValue: formatValueOrNA(currCv, convertedVisitsFormatter),
        change: calculateChangeOrNA(currCv, prevCv, "ConvertedVisits"),
    });
};

export const getTooltipData = (pointOptions: IDataPoint, metaData: IMetaData, type: ETypes, series?: Series) => {
    const { x, y, z } = pointOptions;
    const { xAxis, yAxis, zAxis } = metaData;
    const chartOptionsMetaData = CHART_OPTIONS_META_DATA[type];

    const rawCurrentData = series
        ? ((series.userOptions as unknown as Record<string, unknown>)?.rawCurrentData as Partial<INumbersRecord>)
        : undefined;

    if (rawCurrentData) {
        const [xAxisAttribute, yAxisAttribute, zAxisAttribute] = [
            chartOptionsMetaData.xAxis.attribute,
            chartOptionsMetaData.yAxis.attribute,
            chartOptionsMetaData.zAxis.attribute,
        ];

        const rawX = Number(rawCurrentData[xAxisAttribute]) || 0;
        const rawY = Number(rawCurrentData[yAxisAttribute]) || 0;
        const rawZ = Number(rawCurrentData[zAxisAttribute]) || 0;

        const rows: TooltipRow[] = [
            {
                metric: translate(chartOptionsMetaData.xAxis.tooltipTitleKey),
                value: formatValueOrNA(rawX, chartOptionsMetaData.xAxis.formatter),
                aboveAverage: rawX ? resolveAboveAverageValues(rawX, xAxis.average) : null,
            },
            {
                metric: translate(chartOptionsMetaData.yAxis.tooltipTitleKey),
                value: formatValueOrNA(rawY, chartOptionsMetaData.yAxis.formatter),
                aboveAverage: rawY ? resolveAboveAverageValues(rawY, yAxis.average) : null,
            },
            {
                metric: translate(chartOptionsMetaData.zAxis.tooltipTitleKey),
                value: formatValueOrNA(rawZ, chartOptionsMetaData.zAxis.formatter),
                aboveAverage: rawZ ? resolveAboveAverageValues(rawZ, zAxis.average) : null,
            },
        ];
        appendConvertedVisitsRowForConversionChart(rows, type, rawCurrentData);
        return rows;
    }

    const rows: TooltipRow[] = [
        {
            metric: translate(chartOptionsMetaData.xAxis.tooltipTitleKey),
            value: chartOptionsMetaData.xAxis.formatter(x),
            aboveAverage: resolveAboveAverageValues(x, xAxis.average),
        },
        {
            metric: translate(chartOptionsMetaData.yAxis.tooltipTitleKey),
            value: chartOptionsMetaData.yAxis.formatter(y),
            aboveAverage: resolveAboveAverageValues(y, yAxis.average),
        },
        {
            metric: translate(chartOptionsMetaData.zAxis.tooltipTitleKey),
            value: chartOptionsMetaData.zAxis.formatter(z),
            aboveAverage: resolveAboveAverageValues(z, zAxis.average),
        },
    ];
    appendConvertedVisitsRowForConversionChart(
        rows,
        type,
        series
            ? ((series.userOptions as unknown as Record<string, unknown>)?.rawCurrentData as Partial<INumbersRecord>)
            : undefined,
    );
    return rows;
};

export const getPeriodOverPeriodTooltipData = (series: Series, type: ETypes) => {
    const { x: currentX, y: currentY, z: currentZ } = series.data[0].options;
    const chartOptionsMetaData = CHART_OPTIONS_META_DATA[type];
    const [xAxisAttribute, yAxisAttribute, zAxisAttribute] = [
        chartOptionsMetaData.xAxis.attribute,
        chartOptionsMetaData.yAxis.attribute,
        chartOptionsMetaData.zAxis.attribute,
    ];

    const rawCompareData = (series.userOptions as unknown as Record<string, unknown>)?.rawCompareData;
    const rawCurrentData = (series.userOptions as unknown as Record<string, unknown>)?.rawCurrentData as
        | Partial<INumbersRecord>
        | undefined;

    if (!rawCompareData) {
        const rows: PeriodOverPeriodTooltipRow[] = [
            {
                metric: translate(chartOptionsMetaData.xAxis.tooltipTitleKey),
                previousPeriodValue: translate("apps.common.na"),
                currentPeriodValue: chartOptionsMetaData.xAxis.formatter(Number(currentX)),
                change: translate("apps.common.na"),
            },
            {
                metric: translate(chartOptionsMetaData.yAxis.tooltipTitleKey),
                previousPeriodValue: translate("apps.common.na"),
                currentPeriodValue: chartOptionsMetaData.yAxis.formatter(Number(currentY)),
                change: translate("apps.common.na"),
            },
            {
                metric: translate(chartOptionsMetaData.zAxis.tooltipTitleKey),
                previousPeriodValue: translate("apps.common.na"),
                currentPeriodValue: chartOptionsMetaData.zAxis.formatter(Number(currentZ)),
                change: translate("apps.common.na"),
            },
        ];
        appendConvertedVisitsPeriodOverPeriodRow(rows, type, rawCurrentData, undefined);
        return rows;
    }

    const rawCompareRecord = rawCompareData as Partial<INumbersRecord>;
    const prevXNum = Number(rawCompareRecord[xAxisAttribute]) || 0;
    const prevYNum = Number(rawCompareRecord[yAxisAttribute]) || 0;
    const prevZNum = Number(rawCompareRecord[zAxisAttribute]) || 0;
    const currXNum = Number(currentX) || 0;
    const currYNum = Number(currentY) || 0;
    const currZNum = Number(currentZ) || 0;

    const rows: PeriodOverPeriodTooltipRow[] = [
        {
            metric: translate(chartOptionsMetaData.xAxis.tooltipTitleKey),
            previousPeriodValue: formatValueOrNA(prevXNum, chartOptionsMetaData.xAxis.formatter),
            currentPeriodValue: chartOptionsMetaData.xAxis.formatter(currXNum),
            change: calculateChangeOrNA(currXNum, prevXNum, xAxisAttribute),
        },
        {
            metric: translate(chartOptionsMetaData.yAxis.tooltipTitleKey),
            previousPeriodValue: formatValueOrNA(prevYNum, chartOptionsMetaData.yAxis.formatter),
            currentPeriodValue: chartOptionsMetaData.yAxis.formatter(currYNum),
            change: calculateChangeOrNA(currYNum, prevYNum, yAxisAttribute),
        },
        {
            metric: translate(chartOptionsMetaData.zAxis.tooltipTitleKey),
            previousPeriodValue: formatValueOrNA(prevZNum, chartOptionsMetaData.zAxis.formatter),
            currentPeriodValue: chartOptionsMetaData.zAxis.formatter(currZNum),
            change: calculateChangeOrNA(currZNum, prevZNum, zAxisAttribute),
        },
    ];
    appendConvertedVisitsPeriodOverPeriodRow(rows, type, rawCurrentData, rawCompareRecord);
    return rows;
};

export const getAverageTooltipData = (metaData: IMetaData, type: ETypes) => {
    const { xAxis, yAxis, zAxis } = metaData;
    const chartOptionsMetaData = CHART_OPTIONS_META_DATA[type];
    return [
        {
            metric: translate(chartOptionsMetaData.xAxis.tooltipTitleKey),
            value: chartOptionsMetaData.xAxis.formatter(xAxis.average),
        },
        {
            metric: translate(chartOptionsMetaData.yAxis.tooltipTitleKey),
            value: chartOptionsMetaData.yAxis.formatter(yAxis.average),
        },
        {
            metric: translate(chartOptionsMetaData.zAxis.tooltipTitleKey),
            value: chartOptionsMetaData.zAxis.formatter(zAxis.average),
        },
    ];
};
